<template>
  <div class="image-cell-wrapper">
    <span
      :id="cellId"
      class="image-cell"
      @click="openEditor"
    >
      <img v-if="displaySrc" :src="displaySrc" class="cell-image-thumb" :alt="displayName || fieldLabel || 'Image'" />
      <template v-else>{{ displayName }}</template>
    </span>
    <b-popover
      v-if="popoverVisible"
      :target="cellId"
      :manual="true"
      v-model="popoverVisible"
      placement="top"
      container="body"
      custom-class="cell-editor-popover"
      @hidden="onPopoverHidden"
    >
      <template #title>
        {{ fieldLabel }}
      </template>
      <div class="cell-editor-content">
        <img v-if="previewSrc" :src="previewSrc" class="cell-image-preview" :alt="previewName || fieldLabel || 'Image'" />
        <div v-if="previewName" class="cell-image-name">{{ previewName }}</div>
        <input
          v-if="accept === 'image/*'"
          ref="fileInput"
          type="file"
          accept="image/*"
          class="cell-image-file"
          @change="onFile"
        />
        <input
          v-else
          ref="fileInput"
          type="file"
          :accept="accept || undefined"
          class="cell-image-file"
          @change="onFile"
        />
        <input
          v-if="accept === 'image/*'"
          class="form-control form-control-sm mt-2"
          type="url"
          placeholder="https://example.com/photo.jpg"
          @change="onUrl"
        />
        <div v-if="error" class="cell-image-error">{{ error }}</div>
        <div class="d-flex justify-content-end">
          <b-button
            v-if="canRemove"
            size="sm"
            variant="outline-danger"
            class="me-auto"
            @click="markRemove"
          >Remove</b-button>
          <b-button size="sm" variant="secondary" class="me-1" @click="cancel">Cancel</b-button>
          <b-button size="sm" variant="primary" :disabled="!canSave" @click="save">Save</b-button>
        </div>
      </div>
    </b-popover>
  </div>
</template>

<script>
import { imageDataUrl, imageFilename, imageUploadFromDataUrl } from '../../../shared/image-column'

function readImageFile (file) {

  return new Promise((resolve, reject) => {

    const reader = new FileReader()

    reader.onload = () => resolve(imageUploadFromDataUrl(reader.result, file.name, file.type))
    reader.onerror = () => reject(reader.error || new Error('Could not read that image'))
    reader.readAsDataURL(file)
  })
}

export default {
  name: 'ImageCell',
  props: {
    value: {
      type: [String, Number, Object],
      default: null
    },
    item: {
      type: Object,
      required: true
    },
    fieldKey: {
      type: String,
      required: true
    },
    fieldLabel: {
      type: String,
      default: ''
    },
    accept: {
      type: String,
      default: 'image/*'
    }
  },
  data () {
    return {
      popoverVisible: false,
      pendingUpload: null,
      removeImage: false,
      error: ''
    }
  },
  computed: {
    cellId () {
      const id = (this.item && this.item.id) != null ? this.item.id : Math.random().toString(36).slice(2)
      return `imgcell-${id}-${this.fieldKey}`
    },
    displaySrc () {
      const type = this.item && this.item[`${this.fieldKey}_type`]
      if (type && String(type).indexOf('image/') !== 0) return ''
      return imageDataUrl(this.item, this.fieldKey)
    },
    displayName () {
      return imageFilename(this.item, this.fieldKey) || '—'
    },
    previewSrc () {
      if (this.removeImage) return ''
      if (this.pendingUpload) {
        if (this.pendingUpload.contentType && this.pendingUpload.contentType.indexOf('image/') !== 0) return ''
        return `data:${this.pendingUpload.contentType};base64,${this.pendingUpload.data}`
      }
      return this.displaySrc
    },
    previewName () {
      if (this.removeImage) return ''
      if (this.pendingUpload) return this.pendingUpload.filename || this.pendingUpload.url
      return imageFilename(this.item, this.fieldKey)
    },
    canRemove () {
      return !!(this.pendingUpload || this.displaySrc || imageFilename(this.item, this.fieldKey))
    },
    canSave () {
      return !!(this.pendingUpload || this.removeImage)
    }
  },
  mounted () {
    this.$nextTick(() => this.openEditor())
  },
  beforeUnmount () {
    if (this._onCellEditorOpen) {
      document.removeEventListener('cell-editor-open', this._onCellEditorOpen)
    }
  },
  methods: {
    _registerCloseListener () {
      if (this._onCellEditorOpen) return
      this._onCellEditorOpen = (e) => {
        if (e.detail !== this.cellId && this.popoverVisible) {
          this.popoverVisible = false
          this._unregisterCloseListener()
        }
      }
      document.addEventListener('cell-editor-open', this._onCellEditorOpen)
    },
    _unregisterCloseListener () {
      if (this._onCellEditorOpen) {
        document.removeEventListener('cell-editor-open', this._onCellEditorOpen)
        this._onCellEditorOpen = null
      }
    },
    openEditor () {
      document.dispatchEvent(new CustomEvent('cell-editor-open', { detail: this.cellId }))
      this.pendingUpload = null
      this.removeImage = false
      this.error = ''
      this.popoverVisible = true
      this._registerCloseListener()
    },
    async onFile (event) {
      const file = event.target.files && event.target.files[0]
      this.error = ''
      if (!file) return
      if (this.accept && file.type && file.type.indexOf('image/') !== 0) {
        this.error = 'Choose an image file'
        event.target.value = ''
        return
      }
      try {
        this.pendingUpload = await readImageFile(file)
        this.removeImage = false
      } catch (err) {
        this.error = 'Could not read that image'
      }
    },
    onUrl (event) {
      const url = String(event.target.value || '').trim()
      this.error = ''
      if (!url) return
      this.pendingUpload = { url }
      this.removeImage = false
    },
    markRemove () {
      this.pendingUpload = null
      this.removeImage = true
      this.error = ''
      if (this.$refs.fileInput) this.$refs.fileInput.value = ''
    },
    save () {
      if (!this.canSave) return
      this.$emit('save', {
        item: this.item,
        fieldKey: this.fieldKey,
        newValue: this.removeImage ? null : this.pendingUpload
      })
      this.popoverVisible = false
      this._unregisterCloseListener()
    },
    cancel () {
      this.popoverVisible = false
      this._unregisterCloseListener()
    },
    onPopoverHidden () {
      this._unregisterCloseListener()
      this.$emit('cancel')
    }
  }
}
</script>

<style scoped>
.image-cell-wrapper {
  width: 100%;
}
.image-cell {
  cursor: pointer;
  padding: 4px 8px;
  display: block;
  width: 100%;
  border-radius: 3px;
  min-height: 1.4em;
  max-width: 300px;
}
.image-cell:hover {
  background-color: rgba(154, 209, 43, 0.1);
  outline: 1px solid rgba(154, 209, 43, 0.3);
}
.cell-image-thumb {
  display: block;
  max-height: 36px;
  max-width: 72px;
  object-fit: contain;
  border-radius: 3px;
}
</style>

<style>
.cell-editor-popover .cell-image-preview {
  display: block;
  max-width: 240px;
  max-height: 160px;
  margin-bottom: 8px;
  border-radius: 4px;
}
.cell-editor-popover .cell-image-name {
  font-size: 0.8rem;
  color: #b0b0ae;
  margin-bottom: 8px;
}
.cell-editor-popover .cell-image-file {
  display: block;
  width: 100%;
  margin-bottom: 8px;
  color: #e8e8e6;
  font-size: 0.85rem;
}
.cell-editor-popover .cell-image-error {
  color: #f5a3a3;
  font-size: 0.8rem;
  margin-bottom: 8px;
}
.cell-editor-popover .cell-editor-content {
  min-width: 240px;
}
</style>
